import { jsxs, jsx } from 'react/jsx-runtime';
import { useMemo, useRef } from 'react';
import { styled } from 'styled-components';
import TagsList from './TagsList.js';
import { useMutationTagCreate } from '../mutations/useMutationTagCreate.js';
import { useQueryTags } from '../queries/useQueryTags.js';
import { useMagicSuggestionsContext } from '../../magic/components/MagicSuggestionsContext.js';
import { CloseIcon, SearchIcon } from '@fabric-app/icons/icons';
import { getDefaultRadixPortalContainer } from '@fabric-app/compat';
import { Modal } from '@fabric-app/ui/components/Modal/Modal';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { useInputControls } from '@fabric-app/hooks/useInputControls';
import { isTruthy } from '@fabric-app/utils/guards';
import useModalInactiveState from '@fabric-app/hooks/useModalInactiveState';
import { useRefocusOnBlur } from '@fabric-app/hooks/useRefocusOnBlur';
import { TextInput } from '@fabric-app/ui/components/TextInput/TextInput';
import { useBoolState } from '@fabric-app/hooks/useBooleanState';
import { useFocusOnMount } from '@fabric-app/hooks/useFocusOnMount';

const StyledHeader = styled(Modal.Header).withConfig({
  displayName: "EditTagsModal__StyledHeader",
  componentId: "sc-d6b65e9a-0"
})([
  `border-bottom:none;`
]);
const SubHeader = styled.div.withConfig({
  displayName: "EditTagsModal__SubHeader",
  componentId: "sc-d6b65e9a-1"
})([
  `display:flex;flex-direction:column;gap:20px;padding:20px;border-bottom:1px solid `,
  `;`
], cssVar["color-border-primary"]);
const StyledScrollArea = styled(ScrollArea.Default).withConfig({
  displayName: "EditTagsModal__StyledScrollArea",
  componentId: "sc-d6b65e9a-2"
})([
  `display:grid;`
]);
const StyledForm = styled.form.withConfig({
  displayName: "EditTagsModal__StyledForm",
  componentId: "sc-d6b65e9a-3"
})([
  `display:flex;flex-direction:column;height:max-content;min-width:0;min-height:0;`
]);
const CloseButton = styled.button.withConfig({
  displayName: "EditTagsModal__CloseButton",
  componentId: "sc-d6b65e9a-4"
})([
  `cursor:pointer;color:`,
  `;transition-property:color,background-color,border-color,text-decoration-color,fill,stroke;transition-duration:0.2s;transition-timing-function:ease-in-out;`
], cssVar["color-text-primary"]);
const EditTagsModalContent = ({ selectedTags = [], indeterminateTags = [], onSelect: onSelectProp, selectedResourcesCount }) => {
  const { isMobileView } = useResponsive();
  const inputState = useInputControls();
  const queryTags = useQueryTags(inputState.value);
  const mutationTagCreate = useMutationTagCreate();
  const onSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (inputState.value.length === 0) return;
    const existingTag = queryTags.data?.find((t) => t.name.toLowerCase() === inputState.value.toLowerCase());
    if (existingTag) {
      onSelectProp?.(existingTag, true);
      return;
    }
    if (mutationTagCreate.isPending) return;
    mutationTagCreate.mutateAsync({
      tagName: inputState.value,
      action: "from-resource"
    }, {
      onSuccess: (tag) => {
        onSelectProp?.(tag, true);
      }
    });
  };
  const { suggestions, popSuggestion } = useMagicSuggestionsContext();
  const tags = useMemo(() => {
    if (queryTags.isLoading || !queryTags.data) return [];
    const suggestedTags = (suggestions ?? []).map((s) => s.tag && {
      ...s.tag,
      suggested: true
    }).filter(isTruthy);
    const queryTagsData = queryTags.data.filter((t) => !suggestedTags.find((st) => st.id === t.id || st.name.toLowerCase() === t.name.toLowerCase()) && !selectedTags.find((st) => st.id === t.id || st.name.toLowerCase() === t.name.toLowerCase())).map((t) => ({
      ...t,
      suggested: false
    }));
    const selTags = selectedTags.map((st) => ({
      ...st,
      suggested: st.isDraft
    }));
    const tagsWithCheckedState = [
      ...selTags,
      ...suggestedTags,
      ...queryTagsData
    ].map((tag) => {
      if (indeterminateTags.find((t) => t.id === tag.id)) {
        return {
          ...tag,
          checked: "indeterminate"
        };
      }
      if (selectedTags.find((t) => t.id === tag.id)) {
        return {
          ...tag,
          checked: true
        };
      }
      return {
        ...tag,
        checked: false
      };
    });
    return tagsWithCheckedState.filter((tag) => tag.name.toLowerCase().includes(inputState.value.toLowerCase())).sort((a, b) => {
      if (!!a.checked && !b.checked) return -1;
      else if (!!b.checked && !a.checked) return 1;
      else if (a.checked === "indeterminate") return -1;
      else if (b.checked === "indeterminate") return 1;
      else if (!!a.suggested && !b.suggested) return -1;
      else if (!!b.suggested && !a.suggested) return 1;
      else return a.name.localeCompare(b.name, void 0, {
        sensitivity: "base"
      });
    }).filter((tag, index, whole) => whole.findIndex((t) => tag.id === t.id || tag.name.toLowerCase() === t.name.toLowerCase()) === index);
  }, [
    queryTags.isLoading,
    queryTags.data,
    suggestions,
    indeterminateTags,
    selectedTags,
    inputState.value
  ]);
  const onSelect = async (tag, selected) => {
    const safeTag = !tag.isDraft ? tag : await mutationTagCreate.mutateAsync({
      tagName: tag.name
    });
    const suggestion = suggestions?.find((s) => tag.id === s.id);
    if (suggestion) popSuggestion(suggestion);
    onSelectProp?.(safeTag, selected);
  };
  const formRef = useRef(null);
  const inputRef = useRef(null);
  const isModalInactive = useModalInactiveState(formRef);
  const shouldAutoFocus = !(isModalInactive || isMobileView);
  const onBlurInput = useRefocusOnBlur(!shouldAutoFocus);
  useFocusOnMount(inputRef, {
    condition: shouldAutoFocus
  });
  return /* @__PURE__ */ jsxs(StyledForm, {
    onSubmit,
    ref: formRef,
    children: [
      /* @__PURE__ */ jsx(StyledHeader, {
        children: "Choose Tags"
      }),
      /* @__PURE__ */ jsxs(SubHeader, {
        children: [
          /* @__PURE__ */ jsxs(Modal.Description, {
            children: [
              "Select tags to add or remove from",
              " ",
              selectedResourcesCount === 1 ? "this item" : `selected items`,
              "."
            ]
          }),
          /* @__PURE__ */ jsx(TextInput, {
            placeholder: isMobileView ? "Filter tags" : "Find tags or enter new tag name",
            startAdornment: /* @__PURE__ */ jsx(SearchIcon, {}),
            onChange: inputState.onChange,
            onBlur: onBlurInput,
            value: inputState.value,
            ref: inputRef,
            autoFocus: true,
            inputHeight: "lg",
            endAdornment: inputState.value && /* @__PURE__ */ jsx(CloseButton, {
              type: "button",
              onClick: inputState.clear,
              children: /* @__PURE__ */ jsx(CloseIcon, {
                style: {
                  width: 14,
                  height: 14
                }
              })
            })
          })
        ]
      }),
      /* @__PURE__ */ jsx(StyledScrollArea, {
        type: "always",
        children: /* @__PURE__ */ jsx(Modal.Body, {
          children: /* @__PURE__ */ jsx(TagsList, {
            tags,
            onSelect,
            isLoading: queryTags.isLoading,
            searchQuery: inputState.value
          })
        })
      })
    ]
  });
};
const EditTagsModal = ({ children, ...props }) => {
  const { open, onOpenChange } = "open" in props ? props : {
    open: void 0,
    onOpenChange: void 0
  };
  const { modalProps } = useBoolState();
  const isOpen = open || modalProps.open;
  return /* @__PURE__ */ jsxs(Modal.Root, {
    open: isOpen,
    onOpenChange: onOpenChange || modalProps.onOpenChange,
    children: [
      /* @__PURE__ */ jsx(Modal.Trigger, {
        asChild: true,
        children
      }),
      /* @__PURE__ */ jsxs(Modal.Portal, {
        container: getDefaultRadixPortalContainer(),
        children: [
          /* @__PURE__ */ jsx(Modal.Overlay, {}),
          /* @__PURE__ */ jsx(Modal.Content, {
            respectMobileKeyboard: true,
            children: /* @__PURE__ */ jsx(EditTagsModalContent, {
              ...props
            })
          })
        ]
      })
    ]
  });
};

export { EditTagsModal as default };
//# sourceMappingURL=EditTagsModal.js.map
